<template>
  <b-card v-if="optionSet && optionSet.items" class="mt-3">
    <b-card-title>{{ $t('subscriptionOptions') }}</b-card-title>
    <b-card-sub-title class="mb-3">{{ $t('chooseThePlanThatSuitsYourNeeds') }}</b-card-sub-title>
    
    <b-row>
      <b-col 
        v-for="option in optionSet.items" 
        :key="option.code"
        cols="12" 
        md="4"
        class="mb-3"
      >
        <b-card 
          :class="[
            'option-card h-100',
            { 'selected-option': isCurrentOption(option) },
            { 'default-option': option.default },
            { 'disabled-option': isOptionDisabled(option) }
          ]"
          :border-variant="isCurrentOption(option) ? 'primary' : (option.default ? 'success' : 'secondary')"
        >
          <b-card-title class="d-flex justify-content-between align-items-center">
            <span>{{ $translateLabel(option.label) }}</span>
            <b-badge v-if="option.default" variant="success">{{ $t('default') }}</b-badge>
            <b-badge v-if="isCurrentOption(option)" variant="primary">{{ $t('current') }}</b-badge>
          </b-card-title>
          
          <b-card-text>
            <p class="text-muted">{{ $translateLabel(option.description) }}</p>
            
            <div class="price-container text-center my-3">
              <h2 class="mb-0">
                <span v-if="getPrice(option) === 0">{{ $t('free') }}</span>
                <span v-else>{{ formatPriceDisplay(option) }}</span>
              </h2>
              <small class="text-muted" v-if="getPrice(option) > 0">{{ $t('perMonth') }}</small>
            </div>

            <b-list-group flush v-if="option.policies && option.policies.length > 0">
              <b-list-group-item 
                v-for="policy in option.policies" 
                :key="policy.name"
                class="d-flex justify-content-between align-items-center px-0"
              >
              {{ policy }}
                <small>{{ getPolicyLabel(policy) }}</small>
                <b-badge variant="secondary">{{ formatPolicyValue(policy) }}</b-badge>
              </b-list-group-item>
            </b-list-group>
          </b-card-text>

          <template v-slot:footer>
            <b-button
              v-if="!isCurrentOption(option) && !isOptionDisabled(option) && canSelectOption"
              variant="primary"
              block
              @click="selectOption(option)"
            >
              {{ getPrice(option) === 0 ? $t('selectPlan') : $t('upgrade') }}
            </b-button>
            <b-button
              v-else-if="isCurrentOption(option)"
              variant="outline-primary"
              block
              disabled
            >
              {{ $t('currentPlan') }}
            </b-button>
            <b-button
              v-else-if="isOptionDisabled(option)"
              variant="outline-secondary"
              block
              disabled
            >
              {{ $t('marketplace.unavailable') }}
            </b-button>
          </template>
        </b-card>
      </b-col>
    </b-row>
  </b-card>
</template>

<style scoped>
.option-card {
  transition: all 0.3s ease;
  cursor: pointer;
}

.option-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.selected-option {
  box-shadow: 0 4px 20px rgba(0, 123, 255, 0.3);
}

.default-option {
  border-width: 2px;
}

.disabled-option {
  opacity: 0.5;
}

.disabled-option:hover {
  transform: none;
  box-shadow: none;
}

.price-container {
  padding: 1rem 0;
  border-top: 1px solid #dee2e6;
  border-bottom: 1px solid #dee2e6;
}

.price-container h2 {
  color: #007bff;
  font-weight: bold;
}
</style>

<script>
import Price from '../../plugins/protokol/lib/Price'

export default {
  name: "OptionSetSelector",
  props: {
    optionSet: {
      type: Object,
      required: true
    },
    currentOption: {
      type: String,
      default: null
    },
    integrationId: {
      type: String,
      required: true
    },
    canSelectOption: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    priceHelper() {
      const user = this.$store.getters.getUser
      const billingAddress = user && user.settings && user.settings.billing_address
      const country = billingAddress ? billingAddress.country : 'en_US'
      return new Price(country, this.$store.getters.getLocale)
    },
    currency() {
      return this.priceHelper ? this.priceHelper.getCurrency() : 'USD'
    }
  },
  methods: {
    isCurrentOption(option) {
      // If currentOption prop is provided, use it
      if (this.currentOption) {
        return this.currentOption === option.code;
      }
      
      // Otherwise, check from subscriptions
      const subs = this.$store.getters.getSubscriptions || [];
      const integration = subs.find(s => s.product_id === this.integrationId);
      
      if (integration && integration.option_code) {
        return integration.option_code === option.code;
      }
      
      // If no subscription, default option is current
      return option.default === true;
    },

    isOptionDisabled(option) {
      if (!option) return false;
      
      const currentOption = this.getCurrentOption();
      if (!currentOption) return false;
      
      // Disable current option and any option with level <= current
      return (option.level || 0) <= (currentOption.level || 0);
    },

    getCurrentOption() {
      if (!this.optionSet || !this.optionSet.items) return null;

      // If currentOption prop is provided, find it
      if (this.currentOption) {
        return this.optionSet.items.find(opt => opt.code === this.currentOption);
      }

      // Check from subscriptions
      const subs = this.$store.getters.getSubscriptions || [];
      const integration = subs.find(s => s.product_id === this.integrationId);

      if (integration && integration.option_code) {
        return this.optionSet.items.find(opt => opt.code === integration.option_code);
      }

      // Default option
      return this.optionSet.items.find(opt => opt.default === true);
    },

    getPolicyLabel(policy) {
      if (!policy) return '';

      console.log('Policy:', policy);
      
      if (policy.label && policy.label.locales) {
        return this.$translateLabel(policy.label, policy.code);
      }
      
      // Fallback: format the name nicely
      if (policy.name) {
        return policy.name
          .split('_')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
      }
      
      return '';
    },

    formatPolicyValue(policy) {
      if (!policy) return '';
      
      // Check if this is a size/bytes value
      if (policy.name && policy.name.includes('size')) {
        return this.formatBytes(policy.value);
      }
      return policy.value;
    },

    formatBytes(bytes) {
      if (!bytes || bytes === 0) return '0 Bytes';
      
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      
      return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    },

    getPrice(option) {
      if (!option || !option.price) return 0;
      return this.currency === 'RSD' ? option.price.rsd : option.price.usd;
    },

    formatPriceDisplay(option) {
      const price = this.getPrice(option);
      if (this.currency === 'RSD') {
        return `${price} RSD`;
      }
      return `$${price}`;
    },

    selectOption(option) {
      this.$emit('select-option', option);
      
      // If the option is not free, redirect to checkout
      if (this.getPrice(option) > 0) {
        this.$mainDialog.msgBoxConfirm(
          this.$t('youWillBeRedirectedToCheckout'),
          {
            title: this.$t('upgrade'),
            okVariant: 'primary',
            okTitle: this.$t('continue'),
            cancelTitle: this.$t('cancel')
          }
        ).then(confirmed => {
          if (confirmed) {
            // TODO: Implement checkout redirect with option code
            this.$emit('checkout', {
              integration: this.integrationId,
              option: option.code
            });
          }
        });
      } else {
        // For free options, just update the subscription
        this.$emit('update-option', option);
      }
    }
  }
};
</script>
